import type { RefObject } from "react";
import { useEffect, useRef } from "react";

import type { CompassPromptMenuHandle } from "./CompassPromptMenu";

const stopEvent = (event: KeyboardEvent) => {
  event.preventDefault();
  event.stopPropagation();
};

interface PromptKeydownConfig {
  getMenu: () => CompassPromptMenuHandle | null;
  getUserPrompt: () => string;
  onEscape: () => void;
  onSubmit: (event: KeyboardEvent) => void;
}

export const createPromptKeydownHandler =
  ({ getMenu, getUserPrompt, onEscape, onSubmit }: PromptKeydownConfig) =>
  (event: KeyboardEvent) => {
    if (event.isComposing) {
      return;
    }

    if (event.key === "Escape") {
      stopEvent(event);
      onEscape();
      return;
    }

    const menu = getMenu();

    if (!getUserPrompt() && menu) {
      if (event.key === "ArrowDown") {
        stopEvent(event);
        menu.moveDown();
      } else if (event.key === "ArrowUp") {
        stopEvent(event);
        menu.moveUp();
      } else if (event.key === "Enter" && !event.shiftKey) {
        stopEvent(event);
        menu.selectActive();
      }
      return;
    }

    if (event.key === "Enter" && !event.shiftKey) {
      stopEvent(event);
      onSubmit(event);
    }
  };

export const useNativeInputKeydown = (
  inputRef: RefObject<HTMLTextAreaElement | null>,
  handler: (event: KeyboardEvent) => void,
  inputMountKey: unknown,
) => {
  const handlerRef = useRef(handler);
  handlerRef.current = handler;

  useEffect(() => {
    const input = inputRef.current;
    if (!input) {
      return undefined;
    }

    const listener = (event: KeyboardEvent) => handlerRef.current(event);
    input.addEventListener("keydown", listener, true);
    return () => input.removeEventListener("keydown", listener, true);
  }, [inputRef, inputMountKey]);
};
